<template>
    <div class="relative" @mouseenter="openPreview" @mouseleave="scheduleClose" @focusin="openPreview"
        @focusout="handleFocusOut" @keydown.esc.stop="close">
        <a :href="favoritesUrl" :class="['relative hdr_cart_btn', { active: open }]"
            aria-label="Ver historial de favoritos">
            <span
                class="absolute w-[22px] h-[22px] bg-primary -top-[10px] -right-[11px] rounded-full flex items-center justify-center text-xs leading-none text-white">
                {{ countDisplay }}
            </span>
            <svg class="fill-current text-title dark:text-white w-[19px]" viewBox="0 0 24 20"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                    d="M17.3927 0.0917969C15.4463 0.0917969 13.7401 0.959692 12.4584 2.60171C12.2875 2.8207 12.1351 3.03979 12.0001 3.25198C11.865 3.03974 11.7127 2.8207 11.5417 2.60171C10.2601 0.959692 8.55381 0.0917969 6.60743 0.0917969C2.93056 0.0917969 0.300781 3.17049 0.300781 6.86477C0.300781 11.089 3.7629 15.0701 11.5265 19.7733C11.672 19.8614 11.8361 19.9055 12.0001 19.9055C12.1641 19.9055 12.3281 19.8615 12.4737 19.7733C20.2372 15.0702 23.6994 11.089 23.6994 6.86482C23.6994 3.17246 21.0717 0.0917969 17.3927 0.0917969Z" />
            </svg>
        </a>

        <div v-if="open"
            :class="[
                'hdr_cart_popup favoritos-preview absolute z-50 top-full right-0 sm:right-10 xl:right-0 mt-3 w-[330px] sm:w-[370px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.14)]',
                { 'cart-active': open }
            ]">
            <div class="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div>
                    <div class="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Favoritos</div>
                    <h4 class="mt-1 text-base font-semibold leading-tight text-title dark:text-white">Vista rápida</h4>
                </div>
                <div class="flex items-center gap-2">
                    <span
                        class="inline-flex min-w-[28px] items-center justify-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                        {{ countDisplay }}
                    </span>
                    <button class="text-[11px] font-medium text-slate-500 hover:text-primary" @click.stop="refresh"
                        :disabled="loading">
                        Actualizar
                    </button>
                </div>
            </div>

            <div v-if="!authorized" class="px-4 py-5 text-sm leading-6 text-gray-600 dark:text-gray-200">
                <div class="font-semibold text-title dark:text-white">Tu lista está disponible al iniciar sesión.</div>
                <a :href="loginUrl" class="mt-1 inline-flex text-primary underline">Entrar para ver favoritos</a>
            </div>

            <div v-else-if="loading" class="px-4 py-5 text-sm text-gray-600 dark:text-gray-200">
                Cargando...
            </div>

            <div v-else-if="items.length === 0" class="px-4 py-5 text-sm leading-6 text-gray-600 dark:text-gray-200">
                <div class="font-semibold text-title dark:text-white">Aún no tienes favoritos.</div>
                <p class="mt-1">Guarda productos, servicios, cupones y negocios para revisarlos después.</p>
            </div>

            <div v-else class="favoritos-preview__list max-h-[calc(100vh-11rem)] overflow-y-auto px-3 py-2">
                <article v-for="item in items" :key="itemKey(item)"
                    class="flex gap-3 rounded-xl px-2 py-2 transition hover:bg-slate-50">
                    <a :href="item.url" class="block shrink-0">
                        <img :src="item.image || placeholder" alt=""
                            class="h-12 w-12 rounded-xl border border-slate-100 object-cover">
                    </a>
                    <div class="min-w-0 flex-1">
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span
                                        class="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                        {{ typeLabel(item.type) }}
                                    </span>
                                    <span v-if="item.price" class="text-[11px] font-semibold text-slate-500">
                                        {{ item.price }}
                                    </span>
                                </div>
                                <a :href="item.url"
                                    class="mt-2 block text-sm font-semibold leading-5 text-title transition hover:text-primary">
                                    {{ item.name }}
                                </a>
                            </div>
                            <button class="shrink-0 text-[11px] font-medium text-slate-400 transition hover:text-red-600"
                                @click.stop="remove(item)" :disabled="isRemoving(item)">
                                {{ isRemoving(item) ? 'Quitando...' : 'Quitar' }}
                            </button>
                        </div>
                        <div class="mt-2 flex items-center justify-between gap-3">
                            <a :href="item.url" class="text-[12px] font-semibold text-primary hover:underline">
                                Ver detalle
                            </a>
                            <span class="text-[11px] text-slate-400">Guardado en favoritos</span>
                        </div>
                    </div>
                </article>
            </div>

            <div class="border-t border-slate-100 bg-slate-50/80 px-4 py-3">
                <a :href="favoritesUrl"
                    class="flex items-center justify-between text-sm font-semibold text-title transition hover:text-primary">
                    <span>Ver historial completo</span>
                    <svg class="h-4 w-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true">
                        <path d="M3.33301 8H12.6663" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <path d="M8.66699 4L12.667 8L8.66699 12" stroke="currentColor" stroke-width="1.5"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'Favoritos',
    data() {
        return {
            items: [],
            loading: false,
            authorized: true,
            open: false,
            removing: null,
            closeTimer: null,
            placeholder: '/images/placeholder.png',
            loginUrl: '/web/login',
            favoritesUrl: '/favoritos',
        };
    },
    computed: {
        countDisplay() {
            return this.authorized ? this.items.length : 0;
        },
    },
    mounted() {
        this.refresh();
    },
    beforeUnmount() {
        this.clearCloseTimer();
    },
    methods: {
        itemKey(item) {
            return `${item.type}-${item.id}`;
        },
        typeLabel(type) {
            const labels = {
                cupon: 'Cupon',
                negocio: 'Negocio',
                oferta: 'Oferta',
                producto: 'Producto',
                servicio: 'Servicio',
            };

            return labels[type] || type;
        },
        isRemoving(item) {
            return this.removing === this.itemKey(item);
        },
        openPreview() {
            this.clearCloseTimer();
            this.open = true;

            if (this.authorized && this.items.length === 0 && !this.loading) {
                this.refresh();
            }
        },
        scheduleClose() {
            this.clearCloseTimer();
            this.closeTimer = window.setTimeout(() => {
                this.open = false;
            }, 120);
        },
        clearCloseTimer() {
            if (this.closeTimer) {
                window.clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
        handleFocusOut(event) {
            if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
                this.scheduleClose();
            }
        },
        async refresh() {
            this.loading = true;
            try {
                const { data } = await axios.get('/favoritos');
                this.items = data.items || [];
                this.authorized = true;
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    this.authorized = false;
                    this.items = [];
                }
            } finally {
                this.loading = false;
            }
        },
        close() {
            this.clearCloseTimer();
            this.open = false;
        },
        async remove(item) {
            this.removing = this.itemKey(item);
            try {
                await axios.post('/favoritos/toggle', {
                    type: item.type,
                    id: item.id,
                });
                this.items = this.items.filter((i) => !(i.id === item.id && i.type === item.type));
            } catch (error) {
                // si falla, simplemente mantenemos la lista
            } finally {
                this.removing = null;
            }
        },
    },
};
</script>

<style scoped>
.favoritos-preview__list::-webkit-scrollbar {
    width: 6px;
}

.favoritos-preview__list::-webkit-scrollbar-thumb {
    background: rgba(8, 156, 222, 0.26);
    border-radius: 5px;
}

.favoritos-preview__list::-webkit-scrollbar-track {
    background: transparent;
}
</style>
